import { deleteRecord, listRecords, saveRecord } from '@/services/localDb';
import { buildPrintRequestsForEvent, getPrintAgentStatus, sendTicketToPrint } from '@/services/printing/printAgentService';
import { buildPosTicketPrintPayload } from '@/services/printing/posTicketText';

const PRINT_JOB_STATUS = {
  QUEUED: 'queued',
  SENT: 'sent',
  FAILED: 'failed',
};

let flushPendingJobsPromise = null;
let printAgentUnavailableUntil = 0;
let printAgentUnavailableBackoffMs = 15000;

const PRINT_AGENT_RETRY_BACKOFF_INITIAL_MS = 15000;
const PRINT_AGENT_RETRY_BACKOFF_MAX_MS = 120000;
const PRINT_JOB_RETRY_BACKOFF_INITIAL_MS = 60000;
const PRINT_JOB_RETRY_BACKOFF_MAX_MS = 900000;

function isRetryablePrintError(errorMessage = '') {
  const normalizedMessage = String(errorMessage || '').toLowerCase();
  return normalizedMessage.includes('ewu print agent')
    || normalizedMessage.includes('connection refused')
    || normalizedMessage.includes('failed to fetch')
    || normalizedMessage.includes('network')
    || normalizedMessage.includes('timeout');
}

function nowIso() {
  return new Date().toISOString();
}

function normalizeRestaurantId(value) {
  if (value === undefined || value === null || value === '') return null;
  return String(value);
}

function createJobId() {
  return `print_job_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function nowMs() {
  return Date.now();
}

function isoFromNow(offsetMs = 0) {
  return new Date(nowMs() + Math.max(0, Number(offsetMs) || 0)).toISOString();
}

function getPrintJobRetryBackoffMs(attemptCount = 0) {
  const normalizedAttempts = Math.max(0, Number(attemptCount) || 0);
  return Math.min(
    PRINT_JOB_RETRY_BACKOFF_INITIAL_MS * (2 ** normalizedAttempts),
    PRINT_JOB_RETRY_BACKOFF_MAX_MS
  );
}

function schedulePrintAgentRetryCooldown() {
  printAgentUnavailableUntil = nowMs() + printAgentUnavailableBackoffMs;
  printAgentUnavailableBackoffMs = Math.min(
    printAgentUnavailableBackoffMs * 2,
    PRINT_AGENT_RETRY_BACKOFF_MAX_MS
  );
}

function clearPrintAgentRetryCooldown() {
  printAgentUnavailableUntil = 0;
  printAgentUnavailableBackoffMs = PRINT_AGENT_RETRY_BACKOFF_INITIAL_MS;
}

async function ensurePrintAgentAvailable() {
  if (printAgentUnavailableUntil > nowMs()) {
    return {
      ok: false,
      skipped: true,
      error: 'EWU Print Agent no disponible temporalmente.',
    };
  }

  const status = await getPrintAgentStatus();
  if (!status?.ok || !status?.connected) {
    schedulePrintAgentRetryCooldown();
    return {
      ok: false,
      skipped: true,
      error: status?.error || 'EWU Print Agent no disponible.',
    };
  }

  clearPrintAgentRetryCooldown();
  return { ok: true };
}

function normalizePrintJob(job = {}) {
  const normalizedRestaurantId = normalizeRestaurantId(
    job.restaurant_id ?? job.restaurantId ?? job.metadata?.restaurant_id ?? job.metadata?.restaurantId
  );

  return {
    id: job.id || createJobId(),
    restaurant_id: normalizedRestaurantId,
    printer_name: job.printer_name || null,
    service_id: job.service_id || job.serviceId || null,
    ticket_type: job.ticket_type || 'pos_order',
    content: job.content || '',
    html_content: job.html_content || '',
    metadata: job.metadata || {},
    dedupe_key: job.dedupe_key || job.metadata?.dedupe_key || null,
    source: job.source || 'pos',
    status: job.status || PRINT_JOB_STATUS.QUEUED,
    attempt_count: Number(job.attempt_count || 0),
    next_attempt_at: job.next_attempt_at || job.nextAttemptAt || nowIso(),
    last_error: job.last_error || null,
    created_at: job.created_at || nowIso(),
    updated_at: job.updated_at || nowIso(),
    sent_at: job.sent_at || null,
  };
}

export async function enqueuePosPrintJob(job = {}) {
  const record = normalizePrintJob(job);
  await saveRecord('print_jobs', record);
  return record;
}

export async function listPendingPosPrintJobs(restaurantId = null) {
  const jobs = await listRecords('print_jobs');
  const currentTime = nowMs();
  return jobs
    .filter((job) => job.status !== PRINT_JOB_STATUS.SENT)
    .filter((job) => {
      if (!job.next_attempt_at) return true;
      const nextAttemptAtMs = new Date(job.next_attempt_at).getTime();
      return Number.isNaN(nextAttemptAtMs) || nextAttemptAtMs <= currentTime;
    })
    .filter((job) => !restaurantId || String(job.restaurant_id || '') === String(restaurantId))
    .sort((left, right) => new Date(left.created_at || 0) - new Date(right.created_at || 0));
}

export async function removePosPrintJob(jobId) {
  if (!jobId) return false;
  return deleteRecord('print_jobs', jobId);
}

export async function markPosPrintJobFailed(jobId, errorMessage) {
  if (!jobId) return null;
  const jobs = await listRecords('print_jobs');
  const job = jobs.find((item) => item.id === jobId);
  if (!job) return null;

  const nextJob = {
    ...job,
    status: PRINT_JOB_STATUS.FAILED,
    attempt_count: Number(job.attempt_count || 0) + 1,
    next_attempt_at: isoFromNow(getPrintJobRetryBackoffMs(Number(job.attempt_count || 0))),
    last_error: errorMessage || 'No se pudo enviar el ticket al agente de impresión.',
    updated_at: nowIso(),
  };
  await saveRecord('print_jobs', nextJob);
  return nextJob;
}

export async function markPosPrintJobSent(jobId) {
  if (!jobId) return null;
  const jobs = await listRecords('print_jobs');
  const job = jobs.find((item) => item.id === jobId);
  if (!job) return null;

  const nextJob = {
    ...job,
    status: PRINT_JOB_STATUS.SENT,
    next_attempt_at: null,
    sent_at: nowIso(),
    updated_at: nowIso(),
    last_error: null,
  };
  await saveRecord('print_jobs', nextJob);
  return nextJob;
}

export async function sendPosPrintJob(job = {}) {
  const payload = await buildPosTicketPrintPayload(job);
  const restaurantId = normalizeRestaurantId(
    job.restaurant_id ?? job.restaurantId ?? payload.metadata?.restaurant_id ?? payload.metadata?.restaurantId
  );
  const requests = buildPrintRequestsForEvent(payload, 'pos_order_created');
  if (!requests.length) {
    return {
      ok: false,
      queued: false,
      jobs: [],
      error: 'No hay un destino de impresión configurado para la creación de comandas desde TPV.',
    };
  }

  const queuedJobs = [];
  const sentJobs = [];
  let firstError = '';

  for (const requestPayload of requests) {
    try {
      const response = await sendTicketToPrint(requestPayload);
      if (!response.ok) {
        if (isRetryablePrintError(response.error)) {
          schedulePrintAgentRetryCooldown();
          const queued = await enqueuePosPrintJob({
            ...requestPayload,
            restaurant_id: restaurantId,
            source: job.source || requestPayload.metadata?.source || 'pos',
            service_id: requestPayload.service_id || null,
            status: PRINT_JOB_STATUS.QUEUED,
            last_error: response.error || 'No se ha podido imprimir el ticket.',
          });
          queuedJobs.push(queued);
        }
        firstError = firstError || response.error || 'No se ha podido imprimir el ticket.';
        continue;
      }

      sentJobs.push(await enqueuePosPrintJob({
        ...requestPayload,
        restaurant_id: restaurantId,
        source: job.source || requestPayload.metadata?.source || 'pos',
        service_id: requestPayload.service_id || null,
        status: PRINT_JOB_STATUS.SENT,
        sent_at: nowIso(),
      }));
    } catch (error) {
      if (isRetryablePrintError(error?.message || error)) {
        schedulePrintAgentRetryCooldown();
        const queued = await enqueuePosPrintJob({
          ...requestPayload,
          restaurant_id: restaurantId,
          source: job.source || requestPayload.metadata?.source || 'pos',
          service_id: requestPayload.service_id || null,
          status: PRINT_JOB_STATUS.QUEUED,
          last_error: error?.message || String(error),
        });
        queuedJobs.push(queued);
      }
      firstError = firstError || error?.message || String(error);
    }
  }

  if (queuedJobs.length) {
    return {
      ok: false,
      queued: true,
      jobs: queuedJobs,
      sent_jobs: sentJobs,
      error: firstError || 'No se ha podido imprimir el ticket.',
    };
  }

  return {
    ok: sentJobs.length > 0 && !firstError,
    queued: false,
    jobs: sentJobs,
    error: firstError || '',
  };
}

export async function flushPendingPosPrintJobs(restaurantId = null) {
  if (flushPendingJobsPromise) {
    return flushPendingJobsPromise;
  }

  flushPendingJobsPromise = (async () => {
    const availability = await ensurePrintAgentAvailable();
    if (!availability.ok) {
      return {
        sent: 0,
        failed: 0,
        queued: 0,
        skipped: true,
        error: availability.error || 'EWU Print Agent no disponible.',
      };
    }

    const pendingJobs = await listPendingPosPrintJobs(restaurantId);
    const results = {
      sent: 0,
      failed: 0,
      queued: pendingJobs.length,
    };

    for (const job of pendingJobs) {
      try {
        const response = await sendTicketToPrint({
          printer_name: job.printer_name || null,
          service_id: job.service_id || null,
          ticket_type: job.ticket_type || 'pos_order',
          content: job.content || '',
          html_content: job.html_content || '',
          metadata: job.metadata || {},
        });

        if (response.ok) {
          clearPrintAgentRetryCooldown();
          await markPosPrintJobSent(job.id);
          results.sent += 1;
        } else {
          if (String(response.error || '').toLowerCase().includes('ewu print agent')) {
            schedulePrintAgentRetryCooldown();
            break;
          }
          await markPosPrintJobFailed(job.id, response.error || 'No se pudo reenviar el ticket.');
          results.failed += 1;
        }
      } catch (error) {
        if (String(error?.message || error || '').toLowerCase().includes('ewu print agent')) {
          schedulePrintAgentRetryCooldown();
          break;
        }
        await markPosPrintJobFailed(job.id, error?.message || String(error));
        results.failed += 1;
      }
    }

    return results;
  })();

  try {
    return await flushPendingJobsPromise;
  } finally {
    flushPendingJobsPromise = null;
  }
}

